/** A year: the choice outlives the session, so the next visit to the classic portal still respects it. */
const MAX_AGE_SECONDS = 31536000;

/**
 * The `document.cookie` string that tells the host's redirect which version the user picked. With a `domain`
 * (`.systems.bz`) both hosts read it; an empty one keeps it on this host, as on localhost.
 */
export function versionCookie(name: string, domain: string, value: string): string {
  const scope = domain ? `; Domain=${domain}` : '';
  return `${name}=${value}${scope}; Path=/; Max-Age=${MAX_AGE_SECONDS}; SameSite=Lax; Secure`;
}

/**
 * The `document.cookie` string that expires a host-only cookie of `name`, left by an earlier switch that kept it on
 * this host: the browser would keep sending it next to the domain cookie, and the redirect could read either.
 */
export function hostOnlyCookieExpiry(name: string): string {
  return `${name}=; Path=/; Max-Age=0; SameSite=Lax; Secure`;
}

/**
 * The `document.cookie` string that expires the cookie of `name` on `domain`, left there by an earlier release: the
 * browser would keep sending it next to the host-only cookie, and the redirect could read either.
 */
export function domainCookieExpiry(name: string, domain: string): string {
  return `${name}=; Domain=${domain}; Path=/; Max-Age=0; SameSite=Lax; Secure`;
}

/**
 * The domain to write the cookie for on `hostname`: the configured one where the host sits under it, since new and
 * classic run on separate systems hosts; none elsewhere, as the browser silently drops a cookie for a domain the
 * host is not under (a partner's own domain, localhost).
 */
export function versionCookieDomain(configured: string, hostname: string): string {
  const parent = configured.replace(/^\./, '');
  return parent && (hostname === parent || hostname.endsWith(`.${parent}`)) ? configured : '';
}

/**
 * The parent domain of `hostname` (`.prodata.it` for `dev-bs.prodata.it`), where an earlier release wrote the cookie
 * on a partner host. Empty for a host without a parent below the public suffix (`acodat.com`, localhost) or an IP
 * address, as the browser refuses a domain cookie there.
 */
export function parentDomain(hostname: string): string {
  const labels = hostname.split('.');
  return labels.length < 3 || isIpAddress(hostname) ? '' : `.${labels.slice(1).join('.')}`;
}

/** The value of exactly the cookie `name` in a `document.cookie` string (`a=1; b=2`), or `undefined` without it. */
export function versionCookieValue(cookies: string, name: string): string | undefined {
  const prefix = `${name}=`;
  return cookies.split(';').map(cookie => cookie.trim()).find(cookie => cookie.startsWith(prefix))?.slice(prefix.length);
}

function isIpAddress(hostname: string): boolean {
  return hostname.includes(':') || /^\d+(\.\d+){3}$/.test(hostname);
}
